import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Block, DocIndex } from '@hump/shared-types';
import { docRepository } from '../lib/repository/doc';
import { libraryRepository } from '../lib/repository';
import { getStorage } from '../lib/storage/factory';

/** 显示模式 */
export type DisplayMode = 'bilingual' | 'mixed' | 'original' | 'translation';

/** 阅读设置 */
export interface ReadingSettings {
  fontSize: number;
  lineHeight: number;
  theme: 'light' | 'dark' | 'sepia';
}

interface ReaderState {
  docId: string | null;
  docIndex: DocIndex | null;
  blocks: Block[];
  allBlockIds: string[];
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  displayMode: DisplayMode;
  settings: ReadingSettings;
  settingsOpen: boolean;
  guideOpen: boolean;
  tocOpen: boolean;
  annotationListOpen: boolean;

  loadDoc: (docId: string) => Promise<void>;
  loadMore: () => Promise<void>;
  setDisplayMode: (mode: DisplayMode) => void;
  updateSettings: (partial: Partial<ReadingSettings>) => void;
  setSettingsOpen: (open: boolean) => void;
  setGuideOpen: (open: boolean) => void;
  setTocOpen: (open: boolean) => void;
  setAnnotationListOpen: (open: boolean) => void;
  updateProgress: (blockId: string) => Promise<void>;
}

const defaultSettings: ReadingSettings = {
  fontSize: 16,
  lineHeight: 1.8,
  theme: 'light',
};

export const useReaderStore = create<ReaderState>()(
  persist(
    (set, get) => ({
  docId: null,
  docIndex: null,
  blocks: [],
  allBlockIds: [],
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: null,
  displayMode: 'bilingual',
  settings: defaultSettings,
  settingsOpen: false,
  guideOpen: false,
  tocOpen: false,
  annotationListOpen: false,

  loadDoc: async (docId) => {
    set({ loading: true, error: null, docId, blocks: [], allBlockIds: [], hasMore: false });
    try {
      const docIndex = await docRepository.getDocIndex(docId);
      const allBlockIds = docIndex.blockIds;
      const PAGE_SIZE = 50;
      const firstPageIds = allBlockIds.slice(0, PAGE_SIZE);
      const firstPageBlocks = await Promise.all(
        firstPageIds.map((id) => getStorage().readBlock(docId, id))
      );
      set({
        docIndex,
        allBlockIds,
        blocks: firstPageBlocks,
        hasMore: allBlockIds.length > PAGE_SIZE,
        loading: false,
      });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  loadMore: async () => {
    const { docId, blocks, allBlockIds, loadingMore } = get();
    if (!docId || loadingMore || blocks.length >= allBlockIds.length) return;

    set({ loadingMore: true });
    try {
      const PAGE_SIZE = 50;
      const start = blocks.length;
      const end = Math.min(start + PAGE_SIZE, allBlockIds.length);
      const nextPageIds = allBlockIds.slice(start, end);
      const newBlocks = await Promise.all(
        nextPageIds.map((id) => getStorage().readBlock(docId!, id))
      );
      set((state) => ({
        blocks: [...state.blocks, ...newBlocks],
        hasMore: end < allBlockIds.length,
        loadingMore: false,
      }));
    } catch {
      set({ loadingMore: false });
    }
  },

  setDisplayMode: (mode) => set({ displayMode: mode }),

  updateSettings: (partial) =>
    set((state) => ({ settings: { ...state.settings, ...partial } })),

  setSettingsOpen: (open) => set({ settingsOpen: open }),

  setGuideOpen: (open) => set({ guideOpen: open }),
  setTocOpen: (open) => set({ tocOpen: open }),
  setAnnotationListOpen: (open) => set({ annotationListOpen: open }),

  updateProgress: async (blockId) => {
    const { docId, blocks, docIndex } = get();
    if (!docId || !docIndex) return;
    const blockIdx = blocks.findIndex((b) => b.id === blockId);
    if (blockIdx === -1) return;
    const progress = Math.round(((blockIdx + 1) / blocks.length) * 100) / 100;

    // 只在进度增加时写入（防写风暴）
    if (progress <= docIndex.meta.progress) return;

    const updated = {
      ...docIndex,
      meta: { ...docIndex.meta, progress, updatedAt: new Date().toISOString() },
    };
    set({ docIndex: updated });

    // 写入存储（debounce）
    try {
      await getStorage().writeDocIndex(updated);
      await libraryRepository.updateProgress(docId, progress, blockId);
    } catch {
      // 进度写入失败不中断阅读（静默）
    }
  },
    }),
    {
      name: 'hump-reader-settings',
      // 只持久化设置和模式，不持久化运行时数据（blocks/docIndex 等）
      partialize: (state) => ({
        settings: state.settings,
        displayMode: state.displayMode,
      }),
    }
  )
);

// 注册到 window 供 SelectionTranslate 划词追问使用
if (typeof window !== 'undefined') {
  (window as any).__humpReaderStore = useReaderStore;
}
